import dayjs from 'dayjs'
import { GUEST_COUNT_OPTIONS, isFictitiousRoomTypeName } from '@/models/room-rate/base-rate'
import type { BaseRateGuestCount, BaseRatePriceType } from '@/models/room-rate/base-rate'
import { resolvePriceRankCellTint, resolvePriceRankHeaderColor } from '@/models/room-rate/price-rank'

export const ROOM_RATE_DISPLAY_STORAGE_KEY = 'room-rate-display-prefs'

export type RoomRateDisplayPrefs = {
  priceType: BaseRatePriceType
  guestCount: BaseRateGuestCount
}

export type RoomRateDisplayDateCell = {
  date: string
  price: number | null
  /** 未解決時は null */
  rankShortName: string | null
  /** 未解決時は null（hex または互換値） */
  color: string | null
}

export type RoomRateDisplayRoomType = {
  roomTypeId: string
  name: string
  dates: RoomRateDisplayDateCell[]
}

export type RoomRateDisplayResponse = {
  roomTypes: RoomRateDisplayRoomType[]
}

export type RoomRateDisplayFetchParams = {
  from: string
  chargeType: 'rc' | 'per_person'
  pax?: number
}

export type RoomRateDisplayDayColumn = {
  dateKey: string
  weekday: number
  day: number
  rankShortName: string
  headerColor: string
  tint: string
}

export type RoomRateDisplayRow = {
  roomTypeId: string
  roomTypeName: string
  amounts: (number | null)[]
}

const isBaseRatePriceType = (value: unknown): value is BaseRatePriceType => value === 'rc' || value === 'perPerson'

const isBaseRateGuestCount = (value: unknown): value is BaseRateGuestCount =>
  typeof value === 'number' && GUEST_COUNT_OPTIONS.includes(value as BaseRateGuestCount)

export const toApiRoomRateDisplayParams = (
  from: string,
  priceType: BaseRatePriceType,
  guestCount: BaseRateGuestCount,
): RoomRateDisplayFetchParams => {
  if (priceType === 'rc') {
    return { from, chargeType: 'rc' }
  }
  return { from, chargeType: 'per_person', pax: guestCount }
}

export const toRoomRateDisplayTable = (
  response: RoomRateDisplayResponse,
): { columns: RoomRateDisplayDayColumn[]; rows: RoomRateDisplayRow[] } => {
  const rooms = (response.roomTypes || []).filter(room => !isFictitiousRoomTypeName(room.name))
  const dateCells = rooms[0]?.dates || []

  const columns: RoomRateDisplayDayColumn[] = dateCells.map(cell => {
    const d = dayjs(cell.date)
    return {
      dateKey: cell.date,
      weekday: d.day(),
      day: d.date(),
      rankShortName: cell.rankShortName || '',
      headerColor: resolvePriceRankHeaderColor(cell.color),
      tint: resolvePriceRankCellTint(cell.color),
    }
  })

  const rows: RoomRateDisplayRow[] = rooms.map(room => ({
    roomTypeId: room.roomTypeId,
    roomTypeName: room.name,
    amounts: room.dates.map(cell => cell.price),
  }))

  return { columns, rows }
}

/** 保存が無いときは null。呼び出し側がベースレートの採用区分を初期値にする。 */
export const loadRoomRateDisplayPrefs = (): RoomRateDisplayPrefs | null => {
  try {
    const raw = localStorage.getItem(ROOM_RATE_DISPLAY_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<RoomRateDisplayPrefs>
      if (isBaseRatePriceType(parsed.priceType) && isBaseRateGuestCount(parsed.guestCount)) {
        return { priceType: parsed.priceType, guestCount: parsed.guestCount }
      }
    }
  } catch {
    // 壊れた localStorage は無視して未保存扱いにする
  }
  return null
}

export const saveRoomRateDisplayPrefs = (prefs: RoomRateDisplayPrefs) => {
  try {
    localStorage.setItem(ROOM_RATE_DISPLAY_STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    // quota / プライベートモード等は prefs 非永続化のみ（表示操作は継続）
  }
}
